import { useEffect, useState } from "react";
import { PageSkeleton } from "@/components/PageSkeleton";
import api from "@/lib/api";
import { formatIDR, formatIDRShort, formatDate, formatTime } from "@/lib/format";
import { PeriodFilter } from "@/components/PeriodFilter";
import { usePersistentPeriod } from "@/lib/usePersistentPeriod";

const today = () => new Date().toISOString().slice(0, 10);
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp, TrendingDown, Wallet, Landmark,
  ArrowUpRight, ArrowDownRight, BarChart3, HandCoins, Truck,
} from "lucide-react";
import { DragonIcon } from "@/components/DragonIcon";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar,
} from "recharts";

const KpiCard = ({ label, value, icon: Icon, accent, sub, tip, testid }) => (
  <Card data-testid={testid} className="p-4 relative overflow-hidden group hover:shadow-md transition-shadow">
    <div className={`absolute right-0 top-0 h-20 w-20 rounded-full blur-2xl opacity-20 ${accent.blur}`} />
    <div className="flex items-start justify-between relative gap-2">
      <div className="min-w-0">
        <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground leading-tight">{label}</p>
        <p className="mt-1 font-heading text-lg sm:text-xl font-extrabold tracking-tight truncate" title={tip || undefined}>{value}</p>
        {sub && <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground" title={tip || undefined}>{sub}</p>}
      </div>
      <div className={`grid place-items-center h-9 w-9 rounded-lg shrink-0 ${accent.bg}`}>
        <Icon className={`h-4 w-4 ${accent.text}`} />
      </div>
    </div>
  </Card>
);

const TopChart = ({ title, totalLabel, total, rows, color, testid, totalTestid }) => (
  <Card className="p-3" data-testid={testid}>
    <div className="flex items-start justify-between gap-2 mb-2">
      <h3 className="font-heading text-xs font-semibold">{title}</h3>
      <div className="text-right shrink-0">
        <p className="text-[9px] uppercase tracking-wider text-muted-foreground leading-tight">{totalLabel}</p>
        <p className="font-heading text-xs font-extrabold text-[#C9A227]" data-testid={totalTestid}>{formatIDR(total)}</p>
      </div>
    </div>
    {rows.length === 0 ? (
      <p className="text-xs text-muted-foreground py-3">Belum ada data.</p>
    ) : (
      <ResponsiveContainer width="100%" height={Math.max(110, rows.length * 20)}>
        <BarChart data={rows} layout="vertical" margin={{ left: 4, right: 12, top: 0, bottom: 0 }} barCategoryGap="25%">
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
          <XAxis type="number" tick={{ fontSize: 9 }} tickFormatter={formatIDRShort} stroke="hsl(var(--muted-foreground))" axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 9 }} stroke="hsl(var(--muted-foreground))" axisLine={false} tickLine={false}
            tickFormatter={(v) => (v.length > 17 ? `${v.slice(0, 17)}…` : v)} />
          <Tooltip formatter={(v) => formatIDR(v)} contentStyle={{ borderRadius: 10, border: "1px solid hsl(var(--border))", background: "hsl(var(--popover))", fontSize: 11 }} />
          <Bar dataKey="total" fill={color} radius={[0, 3, 3, 0]} barSize={9} />
        </BarChart>
      </ResponsiveContainer>
    )}
  </Card>
);

export default function Dashboard() {
  const [period, setPeriod, range, setRange] = usePersistentPeriod("dashboard");
  const [summary, setSummary] = useState(null);
  const [chart, setChart] = useState([]);
  const [recent, setRecent] = useState([]);
  const [tops, setTops] = useState(null);

  const qstr = () => period === "custom" ? `period=custom&start=${range.start}&end=${range.end}` : `period=${period}`;

  useEffect(() => {
    const q = qstr();
    api.get(`/dashboard/summary?${q}`).then((r) => setSummary(r.data));
    api.get(`/dashboard/sales-chart?${q}`).then((r) => setChart(r.data));
    api.get(`/transactions?${q}`).then((r) => setRecent(r.data.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).slice(0, 10)));
    api.get(`/dashboard/top-partners?${q}`).then((r) => setTops(r.data));
    /* eslint-disable-next-line */
  }, [period, range.start, range.end]);

  if (!summary) return <PageSkeleton stats={8} rows={5} chart />;

  const chartData = chart.map((c) => ({ ...c, label: formatDate(c.date).slice(0, 6) }));

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
        <div>
          <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight">Ringkasan Eksekutif</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">Tumbuh, Berkembang &amp; Menginspirasi</p>
        </div>
        <PeriodFilter value={period} onChange={setPeriod} range={range} onRangeChange={setRange} />
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <KpiCard testid="kpi-income" label="Omzet" value={formatIDR(summary.total_income)}
          icon={TrendingUp} sub={`${summary.order_count ?? summary.transaction_count} order`}
          tip={`Omzet periode ini: ${formatIDR(summary.total_income)} dari ${summary.order_count ?? summary.transaction_count} order`}
          accent={{ bg: "bg-emerald-100 dark:bg-emerald-950", text: "text-emerald-600 dark:text-emerald-400", blur: "bg-emerald-500" }} />
        <KpiCard testid="kpi-expense" label="Total Pengeluaran" value={formatIDR(summary.total_expense)}
          icon={TrendingDown} sub={`HPP ${formatIDRShort(summary.cogs)}`}
          tip={`Total Pengeluaran ${formatIDR(summary.total_expense)} (HPP ${formatIDR(summary.cogs)})`}
          accent={{ bg: "bg-rose-100 dark:bg-rose-950", text: "text-rose-600 dark:text-rose-400", blur: "bg-rose-500" }} />
        <KpiCard testid="kpi-gross-profit" label="Laba Kotor" value={formatIDR(summary.gross_profit)}
          icon={BarChart3} sub={`Omzet ${formatIDRShort(summary.total_income)} − Modal ${formatIDRShort(summary.modal ?? summary.cogs)} − Cashback ${formatIDRShort(summary.cashback_disbursed || 0)}${summary.total_income ? ` (${(((summary.cashback_disbursed || 0) / summary.total_income) * 100).toFixed(1)}% omzet)` : ""}`}
          tip={`Omzet ${formatIDR(summary.total_income)} − Modal ${formatIDR(summary.modal ?? summary.cogs)} − Cashback ${formatIDR(summary.cashback_disbursed || 0)}${summary.total_income ? ` · Cashback = ${(((summary.cashback_disbursed || 0) / summary.total_income) * 100).toFixed(1)}% dari omzet` : ""}`}
          accent={{ bg: "bg-indigo-100 dark:bg-indigo-950", text: "text-indigo-600 dark:text-indigo-400", blur: "bg-indigo-500" }} />
        <KpiCard testid="kpi-net-profit" label="Laba Bersih" value={formatIDR(summary.net_profit)}
          icon={DragonIcon} sub={`Laba Kotor ${formatIDRShort(summary.gross_profit)} − Pengeluaran ${formatIDRShort(summary.total_expense)}`}
          tip={`Laba Kotor ${formatIDR(summary.gross_profit)} − Pengeluaran ${formatIDR(summary.total_expense)}`}
          accent={{ bg: "bg-emerald-100 dark:bg-emerald-950", text: "text-emerald-600 dark:text-emerald-400", blur: "bg-emerald-500" }} />
        {summary.can_view_cash_bank && (
          <KpiCard testid="kpi-cash" label="Kas Tunai" value={formatIDR(summary.cash)}
            icon={Wallet} sub="Saldo saat ini"
            tip={`Saldo kas tunai: ${formatIDR(summary.cash)}`}
            accent={{ bg: "bg-amber-100 dark:bg-amber-950", text: "text-amber-600 dark:text-amber-400", blur: "bg-amber-500" }} />
        )}
        {summary.can_view_cash_bank && (
          <KpiCard testid="kpi-bank" label="Saldo Bank" value={formatIDR(summary.bank_total)}
            icon={Landmark} sub={`+ E-Wallet ${formatIDRShort(summary.ewallet)}`}
            tip={`Bank ${formatIDR(summary.bank_total)} + E-Wallet ${formatIDR(summary.ewallet)}`}
            accent={{ bg: "bg-sky-100 dark:bg-sky-950", text: "text-sky-600 dark:text-sky-400", blur: "bg-sky-500" }} />
        )}
        <KpiCard testid="kpi-piutang" label="Total Piutang" value={formatIDR(summary.total_piutang || 0)}
          icon={HandCoins} sub="Pelanggan belum bayar"
          tip={`Total piutang pelanggan: ${formatIDR(summary.total_piutang || 0)}`}
          accent={{ bg: "bg-rose-100 dark:bg-rose-950", text: "text-rose-600 dark:text-rose-400", blur: "bg-rose-500" }} />
        <KpiCard testid="kpi-titipan" label="Titipan Belum Dikirim" value={formatIDR(summary.undelivered_value || 0)}
          icon={Truck} sub={`${summary.undelivered_count || 0} pesanan menunggu kirim`}
          tip={`${formatIDR(summary.undelivered_value || 0)} dari ${summary.undelivered_count || 0} pesanan menunggu kirim`}
          accent={{ bg: "bg-amber-100 dark:bg-amber-950", text: "text-amber-600 dark:text-amber-400", blur: "bg-amber-500" }} />
      </div>

      <div className="grid grid-cols-1 gap-3">
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-sm font-semibold">Grafik Penjualan & Pengeluaran</h3>
            <div className="flex items-center gap-3 text-[11px]">
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />Pemasukan</span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-rose-500" />Pengeluaran</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={chartData} margin={{ left: -10, right: 8 }}>
              <defs>
                <linearGradient id="gInc" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gExp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--chart-2))" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="hsl(var(--chart-2))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={formatIDRShort} width={70} />
              <Tooltip formatter={(v) => formatIDR(v)} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--popover))", fontSize: 12 }} />
              <Area type="monotone" dataKey="income" stroke="hsl(var(--chart-1))" strokeWidth={2} fill="url(#gInc)" />
              <Area type="monotone" dataKey="expense" stroke="hsl(var(--chart-2))" strokeWidth={2} fill="url(#gExp)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {tops && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="top-partners">
          <TopChart testid="top-customers-chart" totalTestid="top-customers-total"
            title="Top 10 Pelanggan" totalLabel="Total Seluruh Order Masuk"
            total={tops.total_order_all} rows={tops.top_customers} color="hsl(var(--chart-1))" />
          <TopChart testid="top-suppliers-chart" totalTestid="top-suppliers-total"
            title="Top 10 Supplier" totalLabel="Total Seluruh Tagihan Supplier"
            total={tops.total_supplier_all} rows={tops.top_suppliers} color="#C9A227" />
          <TopChart testid="top-admins-chart" totalTestid="top-admins-total"
            title="Top 10 Admin" totalLabel="Total Seluruh Order Admin"
            total={tops.total_order_all} rows={tops.top_admins || []} color="#10B981" />
        </div>
      )}

      <Card className="p-4">
        <h3 className="font-heading text-sm font-semibold mb-2">Transaksi Terbaru</h3>
        <div className="space-y-0.5">
          {recent.length === 0 && <p className="text-sm text-muted-foreground py-4">Belum ada transaksi.</p>}
          {recent.map((t) => (
            <div key={t._id} className="flex items-center justify-between py-2 border-b border-border last:border-0 gap-2">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className={`grid place-items-center h-8 w-8 rounded-full shrink-0 ${t.type === "income" ? "bg-emerald-100 dark:bg-emerald-950" : "bg-rose-100 dark:bg-rose-950"}`}>
                  {t.type === "income" ? <ArrowUpRight className="h-4 w-4 text-emerald-600" /> : <ArrowDownRight className="h-4 w-4 text-rose-600" />}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium break-words leading-snug">{t.type === "income" && t.customer_name ? t.customer_name : t.category}</p>
                  <p className="text-[11px] text-muted-foreground break-words leading-snug">{t.type === "income" && t.customer_name ? `${t.category} · ` : ""}Admin: {t.created_by_name || "-"} · {formatDate(t.date)}{t.created_at ? ` · ${formatTime(t.created_at)}` : ""} · {t.payment_method}</p>
                </div>
              </div>
              <span className={`text-xs font-semibold whitespace-nowrap ${t.type === "income" ? "text-emerald-600" : "text-rose-600"}`}>
                {t.type === "income" ? "+" : "-"}{formatIDR(t.amount)}
              </span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
